import React, { useState, useEffect, useRef } from 'react';
import { Filter, ChevronDown, Edit3 } from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import EditRemarkDialog from './EditRemarkDialog';
import { Grievance } from '../../services/grievance';
import {
  fetchGrievancesThunk,
  setPage,
  setPageSize,
  setSearchQuery,
  setFilters,
  setSortBy,
  setSortOrder,
  resetFilters,
  setSelectedGrievance,
  setHasFetched,
} from '../../features/grievances/grievanceSlice';
import { validateCustomerSearchInput } from "../../utils/searchValidator";


const ManageGrievance: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    grievances,
    loading,
    page,
    pageSize,
    total,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    selectedGrievance,
    hasFetched,
  } = useAppSelector((state) => state.grievances);

  const [showFilters, setShowFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(filters);
  const [showRemarkDialog, setShowRemarkDialog] = useState(false);
  const { token } = useAppSelector((state) => state.auth);
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };

  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
    filters
  });

  // ✅ Only fetch once on first mount
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder ||
      prev.filters.dateRange.from !== filters.dateRange.from ||
      prev.filters.dateRange.to !== filters.dateRange.to;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchGrievancesThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        filters: {
          ...filters,
          dateRange: { ...filters.dateRange },
        },
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    filters.dateRange.from,
    filters.dateRange.to,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);



  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchGrievancesThunk());

  }, [token, hasFetched, dispatch]);

  const handleRefresh = () => {
    dispatch(setPage(1));
    dispatch(fetchGrievancesThunk());
  };

  // Handlers for filters
  const handleApplyFilters = () => {
    dispatch(setFilters(pendingFilters));
    dispatch(setPage(1));
    dispatch(fetchGrievancesThunk()); // fetch on user action only
  };

  const handleResetFilters = () => {
    setPendingFilters({ dateRange: {} });
    dispatch(resetFilters());
    dispatch(setPage(1));
    dispatch(fetchGrievancesThunk()); // fetch on user action only
  };

  const handleOpenRemarkDialog = (grievance: Grievance) => {
    setShowRemarkDialog(true);
    dispatch(setSelectedGrievance(grievance));
  };

  const handlePendingFilterChange = (key: 'from' | 'to', value: string) => {
    setPendingFilters((prev) => ({
      dateRange: { ...prev.dateRange, [key]: value || undefined },
    }));
  };

  const columns = [
    { id: 'srNo', header: 'Sr. No', cell: ({ row }: any) => (page - 1) * pageSize + row.index + 1, size: 60 },
    { id: 'name', header: 'Name', accessorKey: 'name', size: 120 },
    {
      id: 'contactDetails',
      header: 'Contact Details',
      size: 200,
      cell: ({ row }: any) => (
        <div>
          <div className="font-medium">{row.original.phone || '-'}</div>
          <div className="text-gray-600">{row.original.email || '-'}</div>
        </div>
      ),
    },
    {
      id: 'reason',
      header: 'Reason',
      accessorKey: 'reason',
      size: 200,
      cell: ({ row }: any) => <div className="whitespace-normal break-words max-w-[250px]">{row.original.reason || '-'}</div>,
    },
    {
      id: 'createdAt',
      header: 'Submitted At',
      accessorKey: 'createdAt',
      size: 180,
      cell: ({ row }: any) =>
        new Date(row.original.createdAt).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }),
    },
    {
      id: 'adminRemark',
      header: 'Admin Remark',
      accessorKey: 'adminRemark',
      size: 200,
      cell: ({ row }: any) => (
        <div className="flex items-center justify-between">
          <div className="whitespace-pre-line">{row.original.adminRemark || '-'}</div>
          <button onClick={() => handleOpenRemarkDialog(row.original)} className="ml-2 text-blue-500 hover:text-blue-700 p-1 rounded" title="Edit Remark">
            <Edit3 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full no-scrollbar">
      {/* Filters */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <Button variant="outline" onClick={() => setShowFilters((prev) => !prev)} className="flex items-center" size="sm">
            <Filter size={10} className="mr-1" /> Filters <ChevronDown size={10} className="ml-1" />
          </Button>
        </div>
        {showFilters && (
          <div className="p-2 mb-2 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-3">
                <label className="block mb-1 text-xs font-medium text-gray-700">From Date</label>
                <input type="date" value={pendingFilters.dateRange.from || ''} onChange={(e) => handlePendingFilterChange('from', e.target.value)} className="w-full px-1 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="col-span-3">
                <label className="block mb-1 text-xs font-medium text-gray-700">To Date</label>
                <input type="date" value={pendingFilters.dateRange.to || ''} onChange={(e) => handlePendingFilterChange('to', e.target.value)} className="w-full px-1 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="flex items-end justify-end col-span-12 mt-2 mb-1">
                <Button variant="outline" onClick={handleResetFilters} size="sm" className="mr-1 text-xs py-0.5 h-6 px-2">Reset</Button>
                <Button onClick={handleApplyFilters} size="sm" className="text-xs py-0.5 h-6 px-2">Apply</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={grievances}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => dispatch(setPage(p))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new
          onRefresh={handleRefresh}
          enableRowClick={false}
        />
      </div>

      {showRemarkDialog && selectedGrievance && (
        <EditRemarkDialog
          grievance={selectedGrievance}
          isOpen={showRemarkDialog}
          onClose={() => setShowRemarkDialog(false)}
          onRemarkUpdated={() => dispatch(fetchGrievancesThunk())}
        />
      )}
    </div>
  );
};

export default ManageGrievance;
